import React, { useState } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  IconButton,
  InputAdornment,
  Link,
  Stack,
  styled,
  Alert,
} from "@mui/material";
import {
  Visibility,
  VisibilityOff,
} from "@mui/icons-material";
import { OnboardingLayout } from './onBoardingLayOut';
import { useSignupMutation, useResendConfirmationMutation } from "../../Services/create-account";
import { SignupRequest } from "Models/auth";
import { useNavigate } from "react-router-dom";
import { GoogleLogin } from "@react-oauth/google";
import { useGoogleAuthMutation } from "Services/auth";
import { securityClient } from "securityClient";

const LeftSection = styled(Paper)({
  flex: 1,
  padding: "60px 80px",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  boxShadow: "none",
  borderRadius: 0,
});

const StyledTextField = styled(TextField)({
  marginBottom: "20px",
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
  },
});

const SignUpButton = styled(Button)({
  backgroundColor: "#2979ff",
  color: "white",
  padding: "12px",
  borderRadius: "8px",
  textTransform: "none",
  fontSize: "16px",
  fontWeight: 600,
  marginTop: "10px",
  "&:hover": {
    backgroundColor: "#1565c0",
  },
});

const SetupStep = styled(Box)<{ completed: boolean }>(({ completed }) => ({
  display: "flex",
  alignItems: "center",
  marginBottom: "20px",
  color: completed ? "white" : "rgba(255, 255, 255, 0.5)",
}));

interface FormData {
  firstName: string;
  lastName: string;
  jobTitle: string;
  companyEmail: string;
  password: string;
}

interface FormErrors {
  firstName?: string;
  lastName?: string;
  jobTitle?: string;
  companyEmail?: string;
  password?: string;
}

const validateEmail = (email: string) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

const validatePassword = (password: string): string | undefined => {
  const valid =
    password.length >= 8 &&
    /[A-Z]/.test(password) &&
    /[a-z]/.test(password) &&
    /[0-9]/.test(password) &&
    /[^A-Za-z0-9]/.test(password);
  return valid
    ? undefined
    : "Password must be at least 8 characters and contain an uppercase letter, a lowercase letter, a number, and a special character.";
};

const CreateAccount: React.FC = () => {
  const [formData, setFormData] = useState<FormData>({
    firstName: "",
    lastName: "",
    jobTitle: "",
    companyEmail: "",
    password: "",
  });

  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});
  const [apiError, setApiError] = useState<string | null>(null);
  const [showResendOption, setShowResendOption] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [signup] = useSignupMutation();
  const [resendConfirmation] = useResendConfirmationMutation();
  const [googleAuth] = useGoogleAuthMutation();
  const navigate = useNavigate();

  const handleInputChange =
    (field: keyof FormData) => (event: React.ChangeEvent<HTMLInputElement>) => {
      setFormData({
        ...formData,
        [field]: event.target.value,
      });
      if (errors[field as keyof FormErrors]) {
        setErrors({ ...errors, [field]: undefined });
      }
    };

  const validate = (): boolean => {
    const newErrors: FormErrors = {};
    if (!formData.firstName.trim()) newErrors.firstName = "First name is required.";
    else if (formData.firstName.length > 100) newErrors.firstName = "First name must be 100 characters or fewer.";
    if (!formData.lastName.trim()) newErrors.lastName = "Last name is required.";
    else if (formData.lastName.length > 100) newErrors.lastName = "Last name must be 100 characters or fewer.";
    if (formData.jobTitle.length > 150) newErrors.jobTitle = "Job title must be 150 characters or fewer.";
    if (!formData.companyEmail.trim()) {
      newErrors.companyEmail = "Email is required.";
    } else if (!validateEmail(formData.companyEmail)) {
      newErrors.companyEmail = "Enter a valid email address.";
    }
    const passwordError = validatePassword(formData.password);
    if (passwordError) newErrors.password = passwordError;
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setApiError(null);
    if (!validate()) return;

    const request: SignupRequest = {
      email: formData.companyEmail,
      password: formData.password,
      firstName: formData.firstName,
      lastName: formData.lastName,
      jobTitle: formData.jobTitle,
    };

    try {
      const res = await signup(request).unwrap();

      console.log("UserSub:", res.userSub);
      console.log("CompanyId:", res.companyId);
      console.log("Verification sent to:", res.codeDeliveryDetails.destination);

      localStorage.setItem("activationEmail", formData.companyEmail);
      navigate("/activate-account")

    } catch (error: any) {
      const status = error?.status ?? error?.data?.statusCode;

      if (status === 409) {
        if (error?.data?.confirmed === true) {
          setApiError("This account already exist and verified. Please sign in.");
          setShowResendOption(false);
        } else {
          setApiError(error?.data?.message || "An account with this email already exists but has not been confirmed.");
          setShowResendOption(true);
        }
      } else {
        setApiError(error?.data?.message || error?.message || "Signup failed. Please try again.");
        setShowResendOption(false);
      }
    }
  };

  const handleResend = async () => {
    setIsResending(true);
    setApiError(null);
    try {
      await resendConfirmation({ email: formData.companyEmail }).unwrap();
      localStorage.setItem("activationEmail", formData.companyEmail);
      navigate("/activate-account");
    } catch (resendError: any) {
      const resendStatus = resendError?.status ?? resendError?.data?.statusCode;
      if (resendStatus === 400) {
        setApiError("This account is already confirmed. Please sign in.");
      } else {
        setApiError(resendError?.data?.message || resendError?.message || "Could not resend confirmation. Please try again.");
      }
      setShowResendOption(false);
    } finally {
      setIsResending(false);
    }
  };


  const handleGoogleSuccess = async (idToken: string) => {
    try {
      const result = await googleAuth({ idToken }).unwrap();
      localStorage.setItem("idToken", result.idToken);
      localStorage.setItem("authProvider", "google");
      if (result.refreshToken) localStorage.setItem("refreshToken", result.refreshToken);
      securityClient.setAccessTokenSilently(async () => result.idToken);
      navigate("/connect-hubspot");
    } catch (error: any) {
      setApiError(error?.data?.message || error?.message || "Google sign-in failed. Please try again.");
    }
  };


  return (
    <OnboardingLayout currentStep={0}>
      <LeftSection>
        <Box maxWidth="400px" margin="0 auto">
          <Typography variant="h4" fontWeight={700} gutterBottom>
            Create Your RevOptica Account
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            gutterBottom
            sx={{ mb: 4 }}
          >
            Connect your team's sales insights in minutes.
          </Typography>

          {apiError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {apiError}
              {showResendOption && (
                <Box mt={1}>
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    disabled={isResending}
                    onClick={handleResend}
                    sx={{ textTransform: "none" }}
                  >
                    {isResending ? "Sending..." : "Resend Verification Code"}
                  </Button>
                </Box>
              )}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <StyledTextField
              fullWidth
              placeholder="First Name"
              variant="outlined"
              value={formData.firstName}
              onChange={handleInputChange("firstName")}
              error={!!errors.firstName}
              helperText={errors.firstName}
              inputProps={{ maxLength: 100 }}
            />

            <StyledTextField
              fullWidth
              placeholder="Last Name"
              variant="outlined"
              value={formData.lastName}
              onChange={handleInputChange("lastName")}
              error={!!errors.lastName}
              helperText={errors.lastName}
              inputProps={{ maxLength: 100 }}
            />

            <StyledTextField
              fullWidth
              placeholder="Job Title (Optional)"
              variant="outlined"
              value={formData.jobTitle}
              onChange={handleInputChange("jobTitle")}
              error={!!errors.jobTitle}
              helperText={errors.jobTitle}
              inputProps={{ maxLength: 150 }}
            />

            <StyledTextField
              fullWidth
              placeholder="Company Email"
              variant="outlined"
              type="email"
              value={formData.companyEmail}
              onChange={handleInputChange("companyEmail")}
              error={!!errors.companyEmail}
              helperText={errors.companyEmail}
            />

            <StyledTextField
              fullWidth
              placeholder="Password"
              variant="outlined"
              type={showPassword ? "text" : "password"}
              value={formData.password}
              onChange={handleInputChange("password")}
              error={!!errors.password}
              helperText={errors.password}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <SignUpButton fullWidth type="submit">
              Sign up
            </SignUpButton>
          </form>

          <Box textAlign="center" my={3}>
            <Typography variant="body2" color="text.secondary">
              or
            </Typography>
          </Box>

          <Typography variant="body2" textAlign="center" mb={2}>
            Already have an account?{" "}
            <Link
              component="button"
              underline="hover"
              fontWeight={600}
              onClick={() => navigate("/login")} // or "/signin" depending on your route
            >
              Sign in
            </Link>
          </Typography>

          <Stack direction="row" spacing={2} justifyContent="center">
            <GoogleLogin
              onSuccess={(response) => handleGoogleSuccess(response.credential!)}
              onError={() => console.error("Google signup failed")}
              width="400px"
            />
          </Stack>
        </Box>
      </LeftSection>

      {/* Right Section - Setup Steps
      <RightSection>
        <Box>
          <Typography variant="h5" fontWeight={600} gutterBottom>
            Quick & Easy Setup
          </Typography>

          <Box mt={5}>
            {setupSteps.map((step, index) => (
              <SetupStep key={index} completed={step.completed}>
                {step.completed ? (
                  <CheckCircle sx={{ mr: 2, fontSize: 24 }} />
                ) : (
                  <RadioButtonUnchecked sx={{ mr: 2, fontSize: 24 }} />
                )}
                <Typography variant="body1">{step.label}</Typography>
              </SetupStep>
            ))}
          </Box>
        </Box>
      </RightSection> */}
    </OnboardingLayout>
  );
};

export default CreateAccount;
